import './screens.css';
import './hub.css';
import { el, setText, text } from './walkout.js';
import { createPromoter } from './promoter.js';
import { whatTheyFight } from '../sim/ladder.js';
import { STATS, STYLES, styleCost } from '../core/styles.js';
import { secondaryNote, styleCard } from './styleCard.js';
import { needs, needsIf } from './contract.js';
import { roomFor } from './venueWords.js';

/**
 * Everything between two fights, on one screen.
 *
 * This was the offer screen (step 59): three cards, a purse each, and a shop
 * strip underneath. Everything *else* a career had — the rank, the record, who
 * was above you, what you had spent points on — was either in the debug panel
 * or nowhere. The ladder in particular has been the thing the whole campaign is
 * about since step 49 and had never once been drawn.
 *
 * So: the ladder on the left, the three cards on the right, the money and what
 * it buys underneath, and Sala across the top. The rule the offer screen was
 * built on still holds and is the reason this is a rearrangement rather than a
 * rewrite — **a screen reads; it does not decide.** Which fights are on the
 * table is `campaign.offer`, what they pay is `cards.json`, what a purchase
 * costs and whether it is allowed is `bank.js`, and what Sala says is
 * `sala.json`. Nothing on this screen works anything out.
 *
 * The shop and the gym are split because the two currencies are (step 60).
 * Cash comes from turning up and buys gear; points come only from beating
 * somebody above you and buy the fighter. Putting them in one row was fine when
 * it was a strip; on a screen with room to say so, the split is the mechanic.
 */
export function createHub({
  campaign,
  bank = null,
  /** What the player fights *now* — the roster does not own it after boot. */
  styles = () => campaign.ladder.player.styles ?? [],
  onPick = () => {},
  onSpend = () => {},
  container = document.body,
}) {
  needs(campaign, ['offer', 'accept', 'gate', 'ducking', 'streak', 'wins', 'losses', 'draws', 'ladder'], {
    who: 'the hub',
    what: 'campaign',
  });
  needs(campaign.ladder, ['player', 'ranks', 'size'], { who: 'the hub', what: 'campaign.ladder' });
  // Optional: the hub simply has no shop and no gym without one.
  needsIf(bank, ['cash', 'points', 'catalogue', 'buy', 'training', 'train', 'learn', 'config'], {
    who: 'the hub',
    what: 'bank',
  });

  const root = el('div', 'screen screen--hub');
  root.hidden = true;

  // --- who you are ----------------------------------------------------------
  const head = el('div', 'hub__head');
  const who = el('div', 'hub__who');
  const name = text('div', 'hub__name', '');
  const standing = text('div', 'hub__standing', '');
  // What the rooms paid: income, with the room it came from. Absent for a
  // player who has never held one.
  const takings = text('div', 'hub__takings', '');
  who.append(name, standing, takings);
  const wallet = el('div', 'hub__wallet');
  const cash = stat('cash');
  const points = stat('development');
  wallet.append(cash.root, points.root);
  head.append(who, wallet);
  root.append(head);

  const sala = createPromoter({ campaign, screen: 'offer' });
  root.append(sala.element);

  // --- the ladder, and the three cards --------------------------------------
  const main = el('div', 'hub__main');

  const ladderPanel = el('div', 'hub__panel hub__ladder');
  ladderPanel.append(text('div', 'ui-label', 'the ladder'));
  const ladderList = el('div', 'hub__rungs');
  ladderPanel.append(ladderList);

  const offerPanel = el('div', 'hub__offer');
  const offerHead = el('div', 'hub__offer-head');
  offerHead.append(text('div', 'ui-label', 'tonight'));
  const duck = text('div', 'hub__duck', '');
  offerHead.append(duck);
  const deck = el('div', 'hub__cards');
  offerPanel.append(offerHead, deck);

  main.append(ladderPanel, offerPanel);
  root.append(main);

  // --- what the money is for ------------------------------------------------
  const spend = el('div', 'hub__spend');

  const shop = el('div', 'hub__panel');
  shop.append(text('div', 'ui-label', 'the shop · cash'));
  // How the crowd feels about tonight's look, said before somebody walks out
  // in it — the same reason the reach card warns before it bites.
  const bored = text('div', 'hub__bored', '');
  const gearRow = el('div', 'hub__slots');
  shop.append(bored, gearRow);

  const gym = el('div', 'hub__panel');
  gym.append(text('div', 'ui-label', 'the gym · stats'));
  const trainRow = el('div', 'hub__buys');
  gym.append(trainRow);

  // Its own panel across the full width, because four style cards in a row is
  // shorter than four in a two-by-two and this band is the one that has to fit.
  const school = el('div', 'hub__panel hub__gym--styles');
  const learnHead = text('div', 'hub__gym-head', '');
  const learnRow = el('div', 'hub__styles');
  school.append(text('div', 'ui-label', 'the gym · styles'), learnHead, learnRow);

  if (bank) spend.append(shop, gym, school);
  root.append(spend);

  const note = text('div', 'hub__note', '');
  root.append(note);
  root.append(text('div', 'screen__hint', 'click a card, or press 1, 2 or 3'));

  container.append(root);

  let up = false;
  /** The cards as drawn, in the order the number keys pick them. */
  let drawn = [];

  function show() {
    const offer = campaign.offer;
    if (!offer.length) return false;
    drawn = offer;

    drawWho();
    drawLadder();
    drawCards(offer);
    sala.update();
    drawSpend();
    setText(note, '');

    root.hidden = false;
    up = true;
    return true;
  }

  // --- the panels -----------------------------------------------------------

  function drawWho() {
    const me = campaign.ladder.player;
    const gate = campaign.gate;
    setText(name, me.name);
    const run =
      campaign.streak > 0
        ? `${campaign.streak} in a row`
        : campaign.streak < 0
          ? `${-campaign.streak} straight losses`
          : 'no run going';
    // The rooms they hold, or that they hold none: "no home until they take
    // one" is a fact about the career and the first thing that changes it.
    const rooms = (me.holds ?? []).map((id) => campaign.ladder.venue(id)?.name).filter(Boolean);
    setText(
      standing,
      `rank ${gate.rank} of ${campaign.ladder.size} · ` +
        `${campaign.wins}W ${campaign.losses}L${campaign.draws ? ` ${campaign.draws}D` : ''} · ${run} · ` +
        (gate.open ? 'contender' : `${gate.short} off the card`) +
        (campaign.ladder.venues?.length ? ` · ${rooms.length ? `holds ${rooms.join(', ')}` : 'no home yet'}` : '')
    );
    root.dataset.contender = String(gate.open);
    drawTakings(me);

    if (!bank) return;
    cash.set(`$${bank.cash.toLocaleString()}`);
    points.set(String(bank.points));
  }

  /**
   * The rooms' money, in two halves: what they paid out at the last fight (it
   * is in the wallet now), and what is on the tab for the next one. The tab is
   * said out loud because it is the rule: it is only picked up by fighting,
   * and a room remembers `takings.tab` fights of it.
   */
  function drawTakings(me) {
    const last = campaign.history?.[0]?.takings;
    const owed = campaign.ladder.owed?.(me.id) ?? [];
    const words = (rooms) => rooms.map((room) => `${room.name} $${room.amount.toLocaleString()} (${room.fights} fight${room.fights === 1 ? '' : 's'})`).join(' · ');
    const parts = [];
    if (last?.total > 0) parts.push(`rooms paid $${last.total.toLocaleString()} — ${words(last.rooms)}`);
    if (owed.length) {
      const total = owed.reduce((sum, room) => sum + room.amount, 0);
      parts.push(`on the tab $${total.toLocaleString()}, yours when you next fight — ${words(owed)}`);
    }
    takings.hidden = parts.length === 0;
    setText(takings, parts.join('  ·  '));
  }

  /**
   * Everybody, best first, with the player picked out and the contender line
   * drawn where the gate is. Junior's styles stay concealed here: this is the
   * offer screen, and the walkout is where a fighter stops being a rumour.
   */
  function drawLadder() {
    const threshold = campaign.gate.need;
    ladderList.replaceChildren(
      ...campaign.ladder.ranks.map((entry) => {
        const row = el('div', 'hub__rung');
        row.dataset.you = String(Boolean(entry.player));
        // The line under the last contender, so the gate is a place on the
        // list rather than a number in a sentence.
        row.dataset.gate = String(entry.rank === threshold);
        // The record is public for everybody on the list, including the
        // fighter whose *styles* are not — the first version showed Junior as
        // "unknown" here, which conflated two different secrets and told a lie
        // about a number anybody can count.
        row.dataset.hidden = String(whatTheyFight(entry).concealed);
        // A room by the name of whoever holds it — "the ladder entry records
        // who holds it" — so the four rooms are on the list, not in a sentence.
        const holds = (entry.holds ?? []).map((id) => campaign.ladder.venue(id)?.name).filter(Boolean);
        row.dataset.holds = String(holds.length > 0);
        row.append(
          text('span', 'hub__rung-rank', String(entry.rank)),
          text('span', 'hub__rung-name', entry.name),
          text('span', 'hub__rung-meta', `${entry.record.wins}W ${entry.record.losses}L`)
        );
        if (holds.length) row.append(text('span', 'hub__rung-room', `holds ${holds.join(', ')}`));
        return row;
      })
    );

    // The list is capped and scrolls, so a fighter at the bottom of a long
    // ladder would be looking at somebody else's rung. Put theirs in view
    // rather than trusting the highlight to be on screen.
    // Optional because it is a *view* concern and not every DOM has one —
    // jsdom does not implement `scrollIntoView`, and a screen that cannot be
    // built under test is a screen that cannot be tested.
    const you = ladderList.querySelector('[data-you="true"]');
    you?.scrollIntoView?.({ block: 'nearest' });
  }

  function drawCards(offer) {
    deck.replaceChildren(
      ...offer.map((card, at) => {
        const node = el('button', 'card');
        node.dataset.slot = card.slot;
        node.dataset.act = String(card.kind === 'act');
        node.dataset.pointed = String(card.pointed);

        const fight = whatTheyFight(card.opponent);
        // Where it is fought, and whether a room is on the line in it.
        const room = roomFor(card.venue, { ladder: campaign.ladder, opponentId: card.opponent.id });
        node.dataset.stake = room?.stake ?? 'none';
        node.append(
          text('div', 'card__slot', `${at + 1} · ${card.describe}`),
          text('div', 'card__name', card.kind === 'act' ? card.name : card.opponent.name),
          text('div', 'card__rank', `rank ${card.opponent.rank} · ${card.opponent.borough}`),
          ...(room
            ? [
                text(
                  'div',
                  'card__venue',
                  `at ${room.name}${room.stake === 'take' ? ' · for the room' : room.stake === 'defend' ? ' · defending it' : ''}`
                ),
              ]
            : []),
          text('div', 'card__meta', fight.concealed ? 'nobody knows what he fights' : fight.styles.join(' + ')),
          text('div', 'card__climb', climbWords(card.climb)),
          text('div', 'card__purse', `$${card.purse.toLocaleString()}`)
        );
        node.addEventListener('click', () => take(card.slot));
        return node;
      })
    );

    // Said before the reach is turned down, not after: a cost somebody only
    // learns about afterwards is a bug they cannot distinguish from one.
    const owed = campaign.ducking;
    setText(
      duck,
      owed.free > 0
        ? `turn the reach down ${owed.free} more time${owed.free === 1 ? '' : 's'} and the room starts to notice`
        : 'the room has noticed: turning the reach down costs you heat'
    );
    duck.dataset.warm = String(owed.free === 0);
  }

  function drawSpend() {
    if (!bank) return;

    // A row a slot: what is owned (click one to wear it, or the worn one to
    // take it off), and the next tier up, which is the thing to want.
    gearRow.replaceChildren(
      ...bank.catalogue.map((slot) => {
        const row = el('div', 'hub__slot');
        row.dataset.slot = slot.id;
        const items = el('div', 'hub__slot-items');
        row.append(text('span', 'hub__slot-name', slot.name), items);
        for (const tier of slot.tiers.filter((one) => one.owned)) {
          const chip = buy({
            what: tier.name,
            cost: tier.worn ? 'worn' : 'wear',
            title: tier.worn ? `${tier.describe} — click to take it off` : tier.describe,
            owned: tier.worn,
            go: () =>
              purchase(
                () => bank.wear(slot.id, tier.worn ? null : tier.id),
                tier.worn ? `took off the ${tier.name.toLowerCase()}` : `put on the ${tier.name.toLowerCase()}`
              ),
          });
          chip.dataset.worn = String(tier.worn);
          items.append(chip);
        }
        if (slot.next) {
          const next = buy({
            what: slot.next.name,
            cost: `$${slot.next.cost.toLocaleString()}`,
            title: `${slot.next.describe} · +${slot.next.charisma} charisma`,
            off: !slot.next.affordable,
            go: () => purchase(() => bank.buy(slot.next.id), `bought the ${slot.next.name.toLowerCase()}`),
          });
          next.dataset.next = 'true';
          items.append(next);
        } else {
          items.append(text('span', 'hub__slot-top', 'the top of the shop'));
        }
        return row;
      })
    );
    const look = bank.boredom;
    const worth = bank.charisma();
    bored.dataset.stale = String(look.stale);
    setText(
      bored,
      worth.bonus === 0
        ? 'nothing worn yet — gear raises your charisma, and the crowd notices what you walk out in'
        : `this look: seen in ${look.seen} of your last ${look.memory} fights · the crowd gives it ` +
            `${Math.round(look.freshness * 100)}% · charisma ×${worth.scale.toFixed(2)}` +
            (look.stale ? ' — change something' : '')
    );

    trainRow.replaceChildren(
      ...Object.keys(STATS).map((which) => {
        const step = bank.training(which);
        return buy({
          what: `${which} +`,
          cost: step.maxed ? 'maxed' : `${step.cost}p`,
          title: `${step.bought} of ${step.cap} bought`,
          off: step.maxed || !step.affordable,
          owned: step.maxed,
          go: () => purchase(() => bank.train(which), `trained ${which}`),
        });
      })
    );

    // A style, priced off how many are already carried. The whole card rather
    // than a button, because buying one here and choosing one at creation are
    // the same decision and a shop that said less would be asking for
    // twenty-four points in exchange for a name.
    const known = [...(styles() ?? [])];
    const next = bank.styleFor(known);
    setText(
      learnHead,
      next.full
        ? `you carry ${known.length} styles, which is as many as anybody does`
        : `a ${known.length === 1 ? 'second' : 'third'} style costs ${next.cost}p · ${secondaryNote()}`
    );

    learnRow.replaceChildren(
      ...Object.keys(STYLES.styles)
        .filter((id) => !known.includes(id))
        .map((id) => {
          const card = styleCard(id, { role: 'secondary', cost: next.cost });
          card.disabled = next.full || !next.affordable;
          card.title = next.full
            ? `nobody carries more than ${STYLES.maxStyles} styles`
            : `adds its moves, and half its stats — your primary and your finish do not move`;
          card.addEventListener('click', () =>
            purchase(
              () => bank.learn(id, { known: [...(styles() ?? [])] }),
              `took up ${STYLES.styles[id].name.toLowerCase()}`
            )
          );
          return card;
        })
    );
  }

  /**
   * One purchase. The bank says yes or no and *why*; a refusal is shown rather
   * than swallowed, because something that silently did nothing is
   * indistinguishable from a bug.
   */
  function purchase(go, what) {
    const result = go();
    if (!result?.ok) {
      setText(note, result?.reason ?? 'not now');
      note.dataset.refused = 'true';
      return result;
    }
    setText(note, what);
    note.dataset.refused = 'false';
    onSpend({ ...result, what });
    drawWho();
    drawSpend();
    return result;
  }

  /** One card taken. The campaign decides what it cost; this reports it on. */
  function take(slot) {
    if (!up) return null;
    const taken = campaign.accept(slot);
    if (!taken) return null;
    up = false;
    root.hidden = true;
    onPick(taken);
    return taken;
  }

  function onKey(event) {
    if (!up) return;
    // Not while somebody is typing their name on another screen.
    if (event.target?.tagName === 'INPUT') return;
    const at = ['1', '2', '3'].indexOf(event.key);
    if (at < 0 || !drawn[at]) return;
    event.preventDefault();
    take(drawn[at].slot);
  }

  window.addEventListener('keydown', onKey);

  return {
    element: root,
    get up() {
      return up;
    },
    show,
    take,
    dispose() {
      window.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
}

/** A number with a word under it. */
function stat(label) {
  const root = el('div', 'ui-stat');
  const value = text('div', 'ui-stat__value', '');
  value.dataset.tone = 'money';
  root.append(value, text('div', 'ui-stat__label', label));
  return { root, set: (next) => setText(value, next) };
}

/** One thing you can buy, with what it costs and why it is off. */
function buy({ what, cost, title, off = false, owned = false, go }) {
  const node = el('button', 'buy');
  node.disabled = off;
  node.dataset.owned = String(owned);
  node.title = title ?? '';
  node.append(text('span', 'buy__what', what), text('span', 'buy__cost', cost));
  node.addEventListener('click', go);
  return node;
}

/** How far above the player somebody is, in words rather than a signed number. */
export function climbWords(climb) {
  if (climb > 0) return `${climb} place${climb === 1 ? '' : 's'} above you`;
  if (climb < 0) return `${-climb} place${climb === -1 ? '' : 's'} below you`;
  return 'level with you';
}
